import { getAppViewQueryParams } from '../../helpers/appViewHelper';
import {
  getQueryParamsFromURL,
  isBokaTestServiceURL,
} from '../../helpers/urlHelper';
import {
  type GACustom,
  type EventData,
  type EventPayload,
  type PageViewPayload,
} from './types';
import {
  transformToGACustomPayload,
  type CustomParamsForGAEvent,
} from './utilsGA';

const analyticsApiClient = () => {
  const categoryName = 'self_serve_actions';

  const sendAnalyticsEvent = (
    payload: EventPayload | PageViewPayload,
  ): void => {
    if (window && window.sendEvent) {
      try {
        const sendEventFn = window.sendEvent;
        sendEventFn(payload);
      } catch {
        // Third-party fragment errors must never break app routing.
      }
    }
    if (LOG_ANALYTICS === 'true' && TARGET !== 'prod') {
      // Allow logging analytics payloads in non-prod environments
      console.log('Boka analytics', payload);
    }
  };

  const shouldSkipAnalytics = (): boolean => {
    if (isBokaTestServiceURL()) {
      return true;
    }
    return false;
  };

  /**
   * Sends a regular labeled event.
   * @param {object} data – The log settings.
   * @param {string} data.label – The event label.
   * @param {string} [data.action] – Optional event action. Defauls
   * to the actionName provided on instantiation.
   * @param {Object} [customData] Optional param containing additional data.
   */
  const sendEvent = (
    data: EventData,
    customData?: GACustom,
    eventName?: string,
  ): void => {
    if (shouldSkipAnalytics()) {
      return;
    }

    const { platform } = getAppViewQueryParams();

    const payload: EventPayload = {
      event_category: eventName || categoryName,
      event_label: data.label || '',
      event_action: data.action || 'appointment',
    };

    if (customData?.custom || platform) {
      payload.custom = {
        ...customData?.custom,
        ...(platform && { platform }),
        postal_code: customData?.custom?.postal_code || '',
        step: customData?.custom?.step || '',
      };
    }
    if (customData?.params) {
      payload.params = customData.params;
    }

    sendAnalyticsEvent(payload);
  };

  /**
   * Sends a pageview event with custom parameters if provided.
   * Used upon arrival to a page, route or view f.e.
   * Example of `custom` data:
   * { version: '1.2.3', page_type: 'events', local_store: '123' }
   * @param {Object} [custom] – an optional object for the `custom` prop.
   */
  const sendPageview = (custom?: object): void => {
    if (!window || !window.location || shouldSkipAnalytics()) {
      return;
    }

    const { platform } = getAppViewQueryParams();

    const payload: PageViewPayload = {
      event_category: 'pageview',
      page_path: window.location.pathname || '',
      page_location: window.location.href || '',
    };

    if ((custom && Object.keys(custom).length > 0) || platform) {
      payload.custom = {
        ...custom,
        ...(platform && { platform }),
      };
    }

    const refApp = getQueryParamsFromURL().refApp;
    if (refApp) {
      payload.custom = {
        ...payload.custom,
        ref_app: refApp,
      };
    }

    sendAnalyticsEvent(payload);
  };

  // Episod uses Google Analytics
  const sendEventToEpisod = (
    label: string,
    customData?: CustomParamsForGAEvent,
  ): void => {
    const customPayload = (customData &&
      transformToGACustomPayload(customData)) || { custom: {} };

    const refApp = getQueryParamsFromURL().refApp;
    if (refApp) {
      customPayload.custom = customPayload.custom || {};
      customPayload.custom.ref_app = refApp;
    }

    sendEvent(
      {
        label,
        action: 'appointment',
      },
      customPayload,
    );
  };

  return {
    sendEventToEpisod,
    sendPageview,
  };
};

export default analyticsApiClient;
